import { useThree } from "@react-three/fiber";
import { forwardRef, useImperativeHandle } from "react";
import * as THREE from 'three';

export type ProcessEditorExposerRef = {
    setModelPositionAndRotation: (uuid: string, position: THREE.Vector3Tuple, rotation: THREE.EulerTuple) => void;
    setModelScale: (uuid: string, scale: THREE.Vector3Tuple) => void;
    getModelByUUID: (uuid: string) => THREE.Object3D | undefined;
    getScene: () => THREE.Scene | undefined;
    getRootModelUUIDByUUID: (uuid: string) => string | undefined;
    getModelWorldPositionAndRotationByUUID: (uuid: string) => {
        position: THREE.Vector3;
        rotation: THREE.Euler;
    } | undefined;
    setWorldPositionAndRotationAsLocalByUUID: (
        uuid: string,
        worldPosition: THREE.Vector3,
        worldRotation: THREE.Euler
    ) => THREE.Object3D | undefined;
    getModelName: (uuid: string) => string | undefined;
    setModelMaterial: (uuid: string, material: THREE.Material) => void;
    changeModelvisibility: (uuid: string, visible: boolean) => void;
    getModelvisibility: (uuid: string) => boolean;
    attachToParent: (childUUID: string, parentUUID: string) => void;
};

export const ProcessEditorRefExposer = forwardRef<ProcessEditorExposerRef>((props, ref) => {
    const scene = useThree(state => state.scene);
    
    useImperativeHandle(ref, () => ({
        setModelPositionAndRotation(uuid, position, rotation) {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (object) {
                object.position.x = position[0];
                object.position.y = position[1];
                object.position.z = position[2];

                object.rotation.x = rotation[0];
                object.rotation.y = rotation[1];
                object.rotation.z = rotation[2];
            }
        },
        setModelScale(uuid: string, scale: THREE.Vector3Tuple) {
            const object = scene.getObjectByProperty('uuid', uuid);
            if (!object) return;

            object.scale.set(scale[0], scale[1], scale[2]);
        },
        getModelByUUID: (uuid: string) => {
            return scene.getObjectByProperty('uuid', uuid);
        },
        getScene: () => {
            return scene;
        },
        getRootModelUUIDByUUID: (uuid: string) => {
            if (scene.children) {
                for (const child of scene.children) {
                    let found = false;
                    child.traverse(object => {
                        if (object.uuid === uuid) found = true;
                    });

                    if (found) return child.uuid;
                }
            }

            return undefined;
        },
        getModelWorldPositionAndRotationByUUID: (uuid: string) => {
            scene.updateMatrixWorld(true);

            const model = scene.getObjectByProperty('uuid', uuid);
            if (model) {
                return {
                    position: model.getWorldPosition(new THREE.Vector3()),
                    rotation: new THREE.Euler().setFromQuaternion(model.getWorldQuaternion(new THREE.Quaternion()), model.rotation.order)
                };
            }

            return undefined;
        },
        setWorldPositionAndRotationAsLocalByUUID: (uuid: string, worldPosition?: THREE.Vector3, worldRotation?: THREE.Euler) => {
            if (worldPosition || worldRotation) {
                const object = scene.getObjectByProperty('uuid', uuid);

                if (object) {
                    scene.updateMatrixWorld(true);

                    if (worldPosition) {
                        const localPos = object.worldToLocal(worldPosition.clone());
                        object.position.set(localPos.x, localPos.y, localPos.z);
                    }
                    
                    if (worldRotation) {
                        const worldQuat = new THREE.Quaternion().setFromEuler(worldRotation);
                        const inverseParentQuat = object.getWorldQuaternion(new THREE.Quaternion()).invert();
                        const localQuat = worldQuat.clone().premultiply(inverseParentQuat);
                        const localEuler = new THREE.Euler().setFromQuaternion(localQuat, 'XYZ');

                        object.rotation.set(localEuler.x, localEuler.y, localEuler.z);
                    }

                    return object;
                }
            }

            return undefined;
        },
        getModelName: (uuid: string) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (object) return object.name;

            return undefined;
        },
        setModelMaterial: (uuid: string, material: THREE.Material) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (object && object.material) object.material = material;
        },
        changeModelvisibility: (uuid: string, visible: boolean) => {
            const object = scene.getObjectByProperty('uuid', uuid);
            if (object) object.visible = visible;
        },
        getModelvisibility: (uuid: string) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (object) {
                return object.visible;
            }

            return false;
        },
        attachToParent: (childUUID: string, parentUUID: string) => {
            const child = scene.getObjectByProperty('uuid', childUUID);
            if (!child) return;

            const parent = scene.getObjectByProperty('uuid', parentUUID);
            if (!parent) return;

            parent.attach(child);
        }
    }), [scene]);

    return null;
});